import type { ItemOption } from "@gift-registry/shared";
import { normalizeItemOptions, normalizeItemTags } from "@gift-registry/shared";

export interface SignUpValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface SignInValues {
  email: string;
  password: string;
}

export type SignUpErrors = Partial<Record<keyof SignUpValues, string>>;
export type SignInErrors = Partial<Record<keyof SignInValues, string>>;

export interface ItemDraftValues {
  name: string;
  link: string;
  price: string;
  quantity: string;
  hearts: 0 | 1 | 2 | 3;
  tags: string[];
  options: ItemOption[];
}

export type ItemDraftErrors = Partial<Record<"name" | "link" | "price" | "quantity" | "tags" | "options", string>>;

export function validateSignUpForm(values: SignUpValues): SignUpErrors {
  const errors: SignUpErrors = {};

  if (!values.firstName.trim()) errors.firstName = "First name is required";
  if (!values.lastName.trim()) errors.lastName = "Last name is required";
  if (!isValidEmail(values.email)) errors.email = "Enter a valid email address";
  if (values.password.length < 8) {
    errors.password = "Password must be at least 8 characters";
  }

  return errors;
}

export function validateSignInForm(values: SignInValues): SignInErrors {
  const errors: SignInErrors = {};

  if (!isValidEmail(values.email)) errors.email = "Enter a valid email address";
  if (!values.password.trim()) errors.password = "Password is required";

  return errors;
}

export function normalizeSignUpValues(values: SignUpValues): SignUpValues {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    email: normalizeEmail(values.email),
    password: values.password,
  };
}

export function normalizeSignInValues(values: SignInValues): SignInValues {
  return {
    email: normalizeEmail(values.email),
    password: values.password,
  };
}

export function normalizeItemDraftValues(values: ItemDraftValues): ItemDraftValues {
  return {
    ...values,
    name: values.name.trim(),
    link: values.link.trim(),
    tags: normalizeItemTags(values.tags),
    options: normalizeItemOptions(values.options),
  };
}

export function validateItemDraftValues(values: ItemDraftValues): ItemDraftErrors {
  const errors: ItemDraftErrors = {};

  if (!values.name.trim()) errors.name = "Item name is required";
  if (!values.link.trim()) errors.link = "Item link is required";

  if (values.price.trim()) {
    const price = Number(values.price);
    if (!Number.isFinite(price) || price < 0) {
      errors.price = "Enter a valid price";
    }
  }

  if (values.quantity.trim()) {
    const quantity = Number(values.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) {
      errors.quantity = "Quantity must be a positive integer";
    }
  }

  if (values.options.some((option) => option.key.trim().length > 100 || option.value.trim().length > 100)) {
    errors.options = "Option keys and values must be 100 characters or fewer";
  }

  return errors;
}

export function parseItemPrice(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const price = Number(trimmed);
  if (!Number.isFinite(price) || price < 0) {
    return null;
  }

  return Math.round(price * 100);
}

export function parseItemQuantity(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const quantity = Number(trimmed);
  if (!Number.isInteger(quantity) || quantity < 1) {
    return null;
  }

  return quantity;
}

export function resolvePostAuthDestination(
  search: string,
  fallback = "/dashboard"
): string {
  const params = new URLSearchParams(search);
  const next = params.get("next");
  if (next && isSafeInternalPath(next)) {
    return next;
  }
  return fallback;
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isSafeInternalPath(value: string): boolean {
  return value.startsWith("/") && !value.startsWith("//") && !value.includes("://");
}